// src/components/UrduVoiceAssistant.tsx

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { IconType } from 'react-icons';
import {
  FaTimes,
  FaMinus,
  FaVolumeUp,
  FaVolumeMute,
  FaPaperPlane,
  FaTrash,
  FaHome,
  FaUser,
  FaBriefcase,
  FaFolderOpen,
  FaEnvelope,
  FaGithub,
  FaLinkedin,
  FaWhatsapp,
  FaPhone,
  FaFileDownload,
  FaSun,
  FaMoon,
  FaExpand,
} from 'react-icons/fa';
import AIOrb, { OrbState } from './AIOrb';
import VoiceVisualizer from './VoiceVisualizer';
import VoiceButton from './VoiceButton';
import Conversation from './Conversation';
import { useTheme } from '../hooks/useTheme';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis';
import { useConversation } from '../hooks/useConversation';
import {
  streamChatCompletion,
  buildAssistantMessages,
  hasChatApiKey,
  HistoryMessage,
} from '../services/openai';
import {
  detectActions,
  executeAction,
  AssistantAction,
  PagePath,
} from '../services/navigation';
import { ThemeMode } from '../services/theme';

type AssistantStatus = OrbState;

interface QuickChip {
  label: string;
  icon: IconType;
  action: AssistantAction;
}

const CHIPS: QuickChip[] = [
  { label: 'Home', icon: FaHome, action: { type: 'navigate', path: '/' } },
  { label: 'About', icon: FaUser, action: { type: 'navigate', path: '/about' } },
  { label: 'Services', icon: FaBriefcase, action: { type: 'navigate', path: '/services' } },
  { label: 'Projects', icon: FaFolderOpen, action: { type: 'navigate', path: '/projects' } },
  { label: 'Contact', icon: FaEnvelope, action: { type: 'navigate', path: '/contact' } },
  { label: 'GitHub', icon: FaGithub, action: { type: 'open', target: 'github' } },
  { label: 'LinkedIn', icon: FaLinkedin, action: { type: 'open', target: 'linkedin' } },
  { label: 'WhatsApp', icon: FaWhatsapp, action: { type: 'open', target: 'whatsapp' } },
  { label: 'Email', icon: FaEnvelope, action: { type: 'open', target: 'email' } },
  { label: 'Call', icon: FaPhone, action: { type: 'open', target: 'call' } },
  { label: 'Resume', icon: FaFileDownload, action: { type: 'open', target: 'resume' } },
  { label: 'Dark', icon: FaMoon, action: { type: 'theme', theme: 'dark' } },
  { label: 'Light', icon: FaSun, action: { type: 'theme', theme: 'light' } },
];

// ============================================
// ALL MESSAGES IN PLAIN ENGLISH
// ============================================

function welcomeMessage(): string {
  return (
    'Hello! I am GeniusMate, Sarfraz Ahmed Sheru Bhai\'s AI personal assistant. ' +
    'I completely manage this portfolio. How can I help you today?'
  );
}

function firstVisitGreeting(): string {
  return (
    'Hello! I am GeniusMate, Sarfraz Ahmed Sheru Bhai\'s AI personal assistant. ' +
    'I completely manage this portfolio. How can I help you today?'
  );
}

function getNavigationMessage(page: string): string {
  const messages: Record<string, string> = {
    '/': 'You are on the home page. Sarfraz\'s introduction is here.',
    '/about': 'This is the about page. Sarfraz\'s skills and experience are here.',
    '/services': 'This is the services page. Sarfraz\'s services are listed here.',
    '/projects': 'This is the projects page. Sarfraz\'s projects are displayed here.',
    '/contact': 'This is the contact page. You can reach out to Sarfraz here.',
  };
  return messages[page] || 'I am helping you. What would you like to ask?';
}

function themeChangeMessage(theme: string): string {
  return theme === 'dark' 
    ? 'Dark mode has been activated. The screen is now dark.'
    : 'Light mode has been activated. The screen is now light.';
}

function actionConfirmation(action: string): string {
  const messages: Record<string, string> = {
    'github': 'Opening GitHub page.',
    'linkedin': 'Opening LinkedIn profile.',
    'whatsapp': 'Opening WhatsApp chat.',
    'email': 'Opening email compose.',
    'call': 'Making a phone call.',
    'resume': 'Downloading resume.',
  };
  return messages[action] || 'Performing action.';
}

function configNotice(): string {
  return (
    'Sorry! I am not connected to Groq yet. ' +
    'Please set REACT_APP_GROQ_API_KEY in your .env file ' +
    'and reload the app.'
  );
}

function errorNotice(): string {
  return 'Sorry, something went wrong. Please try again.';
}

function apiErrorMessage(error: unknown): string {
  const msg = error instanceof Error ? error.message : '';
  if (/429|rate_limit|too many requests|tokens per day|tpd/i.test(msg)) {
    return 'I am receiving too many requests right now. Please wait a moment and try again.';
  }
  if (/401|api[_-]?key|unauthorized/i.test(msg)) {
    return configNotice();
  }
  return errorNotice();
}

function getSpeechRecognitionError(message: string): string {
  const errors: Record<string, string> = {
    'not-allowed': 'Microphone access denied. Please allow microphone in browser settings.',
    'no-speech': 'No speech detected. Please speak again.',
    'audio-capture': 'Cannot capture audio. Check if microphone is connected properly.',
    'network': 'Network issue. Please check your internet connection.',
    'aborted': 'Speech recognition was stopped.',
    'language-not-supported': 'Language not supported. Please speak in Urdu or English.',
    'service-not-allowed': 'Speech recognition service not allowed. Check browser settings.',
  };
  return errors[message] || `Speech recognition error: ${message}. Please try again.`;
}

function statusLabel(status: AssistantStatus): string {
  switch (status) {
    case 'listening':
      return '🎤 Listening...';
    case 'speaking':
      return '🔊 Speaking...';
    case 'thinking':
      return '⚡ Thinking...';
    default:
      return '💬 Ask me anything';
  }
}

function getInputPlaceholder(): string {
  return 'Ask me anything...';
}

// ============================================
// END OF PLAIN ENGLISH MESSAGES
// ============================================

interface IconButtonProps {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}

const IconButton: React.FC<IconButtonProps> = ({ onClick, label, children }) => (
  <motion.button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className="text-white/80 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/10"
    whileHover={{ scale: 1.12 }}
    whileTap={{ scale: 0.9 }}
  >
    {children}
  </motion.button>
);

const UrduVoiceAssistant: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dragControls = useDragControls();
  const constraintsRef = useRef<HTMLDivElement>(null);

  const { mode, setMode } = useTheme();
  const {
    messages,
    addUserMessage,
    addAssistantMessage,
    updateAssistantMessage,
    finalizeAssistantMessage,
    clear,
  } = useConversation();

  const voiceResultRef = useRef<(text: string) => void>(() => undefined);
  const voiceErrorRef = useRef<(message: string) => void>(() => undefined);

  const {
    isListening: isRecListening,
    interimTranscript,
    start: startRec,
    stop: stopRec,
    isSupported: isSpeechSupported,
  } = useSpeechRecognition({
    // Roman Urdu is written with Latin letters, so the English recognizer
    // transcribes it best; the Groq model understands Roman Urdu text well.
    lang: 'en-US',
    onResult: (text) => voiceResultRef.current(text),
    onError: (message) => voiceErrorRef.current(message),
  });

  // Check if speech recognition is supported
  useEffect(() => {
    if (!isSpeechSupported) {
      const errorMsg = 'Browser does not support speech recognition. Please use Chrome or Edge browser.';
      const id = addAssistantMessage(errorMsg);
      finalizeAssistantMessage(id);
    }
  }, [isSpeechSupported, addAssistantMessage, finalizeAssistantMessage]);

  const { isSpeaking: isTtsSpeaking, speak: speakTTS, stop: stopTTS } = useSpeechSynthesis();

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [status, setStatus] = useState<AssistantStatus>('idle');
  const [inputText, setInputText] = useState('');
  const [muted, setMuted] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const modeRef = useRef(mode);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  const mutedRef = useRef(muted);
  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  const greetingSpokenRef = useRef(false);

  // Speaks text aloud in English.
  const speakEnglish = useCallback(
    (text: string, onEnd?: () => void) => {
      if (mutedRef.current) {
        onEnd?.();
        return;
      }
      speakTTS(text, { lang: 'en', ...(onEnd ? { onEnd } : {}) });
    },
    [speakTTS],
  );

  const speakGreeting = useCallback(() => {
    if (mutedRef.current || greetingSpokenRef.current) return;
    const greeting = firstVisitGreeting();
    speakTTS(greeting, {
      lang: 'en',
      onStart: () => {
        greetingSpokenRef.current = true;
        console.log('🎤 Speaking greeting...');
      },
      onEnd: () => {
        console.log('✅ Greeting finished');
      },
      onError: () => {
        console.log('❌ Greeting error');
      },
    });
  }, [speakTTS]);

  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  // ============================================
  // ON LOAD: Clears any previous chat, opens the assistant,
  // shows a default greeting, and speaks it 1 second after load
  // ============================================
  const bootRef = useRef(false);

  useEffect(() => {
    if (!bootRef.current) {
      bootRef.current = true;
      clear();
      setIsOpen(true);
      setIsMinimized(false);

      const id = addAssistantMessage(firstVisitGreeting());
      finalizeAssistantMessage(id);
    }

    const speakTimer = window.setTimeout(() => {
      speakGreeting();
    }, 1000);

    return () => window.clearTimeout(speakTimer);
  }, [clear, addAssistantMessage, finalizeAssistantMessage, speakGreeting]);

  const submit = useCallback(
    async (raw: string) => {
      const text = raw.trim();
      if (!text) return;

      const currentPath = pathnameRef.current as PagePath;
      const actions = detectActions(text, currentPath);

      if (actions.length === 1 && actions[0].type === 'stopSpeaking') {
        stopTTS();
        return;
      }

      actions.forEach((action) => {
        executeAction(action, navigate, currentPath);
        if (action.type === 'theme') {
          setMode(action.theme as ThemeMode);
          const themeMsg = themeChangeMessage(action.theme as string);
          speakEnglish(themeMsg);
        }
        if (action.type === 'open') {
          const actionMsg = actionConfirmation(action.target);
          speakEnglish(actionMsg);
        }
        if (action.type === 'stopSpeaking') {
          stopTTS();
        }
      });

      addUserMessage(text);

      const history: HistoryMessage[] = [
        ...messagesRef.current
          .filter((m) => !m.streaming && m.content.trim().length > 0)
          .map((m) => ({ role: m.role, content: m.content })),
        { role: 'user', content: text },
      ];

      const assistantId = addAssistantMessage('');
      setStatus('thinking');

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        if (!hasChatApiKey()) {
          throw new Error('API_KEY_MISSING');
        }

        const apiMessages = buildAssistantMessages(
          {
            pathname: pathnameRef.current,
            theme: modeRef.current,
            language: 'en',
          },
          history,
        );

        let accumulated = '';
        let streamFailed = false;
        await streamChatCompletion(
          apiMessages,
          {
            onChunk: (delta) => {
              accumulated += delta;
              updateAssistantMessage(assistantId, accumulated);
            },
            onDone: () => finalizeAssistantMessage(assistantId),
            onError: (error) => {
              console.error('Stream error:', error);
              streamFailed = true;
              const message = apiErrorMessage(error);
              updateAssistantMessage(assistantId, message);
              finalizeAssistantMessage(assistantId);
              setStatus('idle');
              if (!mutedRef.current) {
                speakEnglish(message);
              }
            },
          },
          controller.signal,
        );

        if (controller.signal.aborted) return;
        if (streamFailed) return;

        if (mutedRef.current) {
          setStatus('idle');
          return;
        }

        setStatus('speaking');
        speakEnglish(accumulated, () => setStatus('idle'));
      } catch (error) {
        if (controller.signal.aborted) return;
        
        let message: string;
        if (error instanceof Error && error.message === 'API_KEY_MISSING') {
          message = configNotice();
        } else if (!hasChatApiKey()) {
          message = configNotice();
        } else {
          message = errorNotice();
        }
        
        updateAssistantMessage(assistantId, message);
        finalizeAssistantMessage(assistantId);
        setStatus('idle');
        if (!mutedRef.current) {
          speakEnglish(message);
        }
      } finally {
        abortRef.current = null;
      }
    },
    [
      navigate,
      setMode,
      stopTTS,
      addUserMessage,
      addAssistantMessage,
      updateAssistantMessage,
      finalizeAssistantMessage,
      speakEnglish,
    ],
  );

  const handleVoiceResult = useCallback(
    (text: string) => {
      if (text && text.trim().length > 0) {
        void submit(text);
      } else {
        // No speech detected
        setStatus('idle');
      }
    },
    [submit],
  );

  const handleRecError = useCallback(
    (message: string) => {
      console.error('🎤 Speech recognition error:', message);
      setStatus('idle');

      const userMessage = getSpeechRecognitionError(message);
      const id = addAssistantMessage(userMessage);
      finalizeAssistantMessage(id);
    },
    [addAssistantMessage, finalizeAssistantMessage],
  );

  useEffect(() => {
    voiceResultRef.current = handleVoiceResult;
    voiceErrorRef.current = handleRecError;
  }, [handleVoiceResult, handleRecError]);

  const startListening = useCallback(() => {
    if (!isSpeechSupported) {
      const errorMsg = 'Speech recognition not supported. Please use Chrome browser.';
      const id = addAssistantMessage(errorMsg);
      finalizeAssistantMessage(id);
      return;
    }
    
    if (isRecListening) {
      console.log('🎤 Already listening');
      return;
    }
    
    stopTTS();
    setStatus('listening');
    
    try {
      startRec();
    } catch (error) {
      console.error('❌ Failed to start speech recognition:', error);
      setStatus('idle');
      const errorMsg = 'Cannot start speech recognition. Please try again.';
      const id = addAssistantMessage(errorMsg);
      finalizeAssistantMessage(id);
    }
  }, [isRecListening, stopTTS, startRec, isSpeechSupported, addAssistantMessage, finalizeAssistantMessage]);

  const handleMicToggle = useCallback(() => {
    if (isRecListening) {
      stopRec();
      setStatus('idle');
      return;
    }
    startListening();
  }, [isRecListening, stopRec, startListening]);

  const handleSend = useCallback(() => {
    const text = inputText.trim();
    if (!text) return;
    setInputText('');
    void submit(text);
  }, [inputText, submit]);

  const openAssistant = useCallback(() => {
    setIsMinimized(false);
    setIsOpen(true);
    if (messagesRef.current.length === 0) {
      const id = addAssistantMessage(welcomeMessage());
      finalizeAssistantMessage(id);
      speakEnglish(welcomeMessage());
    }
  }, [addAssistantMessage, finalizeAssistantMessage, speakEnglish]);

  const closeAssistant = useCallback(() => {
    setIsOpen(false);
    abortRef.current?.abort();
    stopTTS();
    stopRec();
    setStatus('idle');
  }, [stopTTS, stopRec]);

  const minimizeAssistant = useCallback(() => {
    setIsMinimized(true);
    abortRef.current?.abort();
    stopTTS();
    stopRec();
    setStatus('idle');
  }, [stopTTS, stopRec]);

  const handleClear = useCallback(() => {
    abortRef.current?.abort();
    stopTTS();
    clear();
    setStatus('idle');
  }, [stopTTS, clear]);

  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      if (next) {
        stopTTS();
        stopRec();
        setStatus('idle');
      }
      return next;
    });
  }, [stopTTS, stopRec]);

  const runChip = useCallback(
    (action: AssistantAction) => {
      executeAction(action, navigate, pathnameRef.current as PagePath);
      if (action.type === 'theme') {
        setMode(action.theme as ThemeMode);
        const themeMsg = themeChangeMessage(action.theme as string);
        speakEnglish(themeMsg);
      }
      if (action.type === 'open') {
        const actionMsg = actionConfirmation(action.target);
        speakEnglish(actionMsg);
      }
      if (action.type === 'navigate') {
        const navMsg = getNavigationMessage(action.path);
        speakEnglish(navMsg);
      }
    },
    [navigate, setMode, speakEnglish],
  );

  // Reset to idle when speech recognition naturally stops (after one utterance).
  useEffect(() => {
    if (!isRecListening && status === 'listening') {
      setStatus('idle');
    }
  }, [isRecListening, status]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const visualizerActive = status === 'listening' || status === 'speaking';
  const isTalking = isRecListening || isTtsSpeaking;

  return (
    <>
      <div ref={constraintsRef} className="fixed inset-0 z-[9997] pointer-events-none" />

      {!isOpen && (
        <motion.button
          type="button"
          onClick={openAssistant}
          aria-label="Open AI assistant"
          title="Open AI assistant"
          className="fixed bottom-6 right-6 z-[9998] p-1 rounded-full bg-gradient-to-br from-primary to-secondary shadow-[0_0_30px_rgba(108,99,255,0.55)] hover:shadow-[0_0_45px_rgba(108,99,255,0.8)] transition-shadow"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1.2, type: 'spring', stiffness: 260, damping: 20 }}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.9 }}
        >
          <AIOrb state={status} size={58} />
          {isRecListening && (
            <span className="absolute top-0 right-0 w-3.5 h-3.5 bg-rose-500 rounded-full animate-pulse border-2 border-white" />
          )}
        </motion.button>
      )}

      <AnimatePresence>
        {isOpen &&
          (isMinimized ? (
            <motion.div
              key="mini"
              drag
              dragListener={false}
              dragControls={dragControls}
              dragMomentum={false}
              dragConstraints={constraintsRef}
              className="fixed bottom-24 right-6 z-[9999] assistant-surface rounded-2xl overflow-hidden w-[min(88vw,240px)]"
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.9 }}
              transition={{ duration: 0.25 }}
            >
              <div className="flex items-center gap-2 px-3 py-2">
                <div
                  className="cursor-grab active:cursor-grabbing"
                  onPointerDown={(e) => dragControls.start(e)}
                >
                  <AIOrb state={status} size={34} />
                </div>
                <span className="flex-1 truncate text-xs font-medium text-gray-700 dark:text-gray-200">
                  {statusLabel(status)}
                </span>
                <IconButton onClick={() => setIsMinimized(false)} label="Expand">
                  <FaExpand size={14} />
                </IconButton>
                <IconButton onClick={closeAssistant} label="Close">
                  <FaTimes size={14} />
                </IconButton>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="full"
              drag
              dragListener={false}
              dragControls={dragControls}
              dragMomentum={false}
              dragConstraints={constraintsRef}
              className="fixed bottom-24 right-6 z-[9999] w-[min(92vw,400px)] md:w-[400px] assistant-surface rounded-3xl overflow-hidden"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.95 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-primary to-secondary px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <div
                    className="flex items-center gap-3 flex-1 min-w-0 cursor-grab active:cursor-grabbing select-none"
                    onPointerDown={(e) => dragControls.start(e)}
                  >
                    <AIOrb state={status} size={40} />
                    <div className="min-w-0">
                      <h3 className="text-white font-display font-semibold text-sm leading-tight flex items-center gap-1.5">
                        GeniusMate
                        <span className="text-[9px] font-medium bg-white/20 px-1.5 py-0.5 rounded-full">
                          AI
                        </span>
                      </h3>
                      <p className="text-white/80 text-[11px] truncate">
                        {statusLabel(status)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <IconButton onClick={toggleMute} label={muted ? 'Unmute' : 'Mute'}>
                      {muted ? <FaVolumeMute size={15} /> : <FaVolumeUp size={15} />}
                    </IconButton>
                    <IconButton onClick={handleClear} label="Clear conversation">
                      <FaTrash size={14} />
                    </IconButton>
                    <IconButton onClick={minimizeAssistant} label="Minimize">
                      <FaMinus size={15} />
                    </IconButton>
                    <IconButton onClick={closeAssistant} label="Close">
                      <FaTimes size={16} />
                    </IconButton>
                  </div>
                </div>
              </div>

              {/* Waveform */}
              <div className="px-4 pt-3 flex items-center justify-center">
                <VoiceVisualizer active={visualizerActive} />
              </div>

              {isRecListening && interimTranscript && (
                <p className="text-center text-xs text-gray-500 dark:text-gray-400 px-4 pt-1 truncate">
                  {interimTranscript}
                </p>
              )}

              {/* Error banner if speech recognition not supported */}
              {!isSpeechSupported && (
                <div className="mx-3 my-2 p-2 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded-lg text-xs text-center">
                  ⚠️ Speech recognition not supported. Please use Chrome browser.
                </div>
              )}

              {/* Messages */}
              <div className="px-3 h-[300px]">
                <Conversation
                  messages={messages}
                  isThinking={status === 'thinking'}
                  className="h-full py-3"
                />
              </div>

              {/* Quick action chips */}
              <div className="px-3 pb-2">
                <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-1">
                  {CHIPS.map((chip) => (
                    <button
                      key={chip.label}
                      type="button"
                      onClick={() => runChip(chip.action)}
                      className="assistant-chip text-[11px] px-2.5 py-1.5 rounded-full flex items-center gap-1 whitespace-nowrap shrink-0"
                    >
                      <chip.icon className="text-[10px]" />
                      {chip.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Input row */}
              <div className="px-3 pb-3 pt-2 flex items-center gap-2 border-t border-gray-200/60 dark:border-white/10">
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSend();
                  }}
                  placeholder={getInputPlaceholder()}
                  aria-label="Message"
                  className="flex-1 min-w-0 bg-white/70 dark:bg-white/10 border border-gray-200 dark:border-white/15 rounded-full px-4 py-2.5 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                <VoiceButton
                  isListening={isRecListening}
                  isProcessing={isTalking && status === 'thinking'}
                  onClick={handleMicToggle}
                  disabled={!isSpeechSupported}
                />
                <motion.button
                  type="button"
                  onClick={handleSend}
                  aria-label="Send message"
                  title="Send"
                  disabled={!inputText.trim()}
                  className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 bg-gradient-to-r from-primary to-secondary text-white shadow-lg shadow-primary/30 hover:shadow-primary/50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaPaperPlane className="text-lg" />
                </motion.button>
              </div>
            </motion.div>
          ))}
      </AnimatePresence>
    </>
  );
};

export default UrduVoiceAssistant;